import { FC, Suspense, useContext, useEffect, useMemo } from 'react';
import SelectedConversationSection from '../components/selected-conversation/SelectedConversationSection';
import AllConversationsSection from '../components/AllConversationsSection';
import {
  AssociateApprovalStatus,
  useAddConversationParticipant,
  useGetAssociateSavedReplies,
  useGetAssociateSettings,
  AuthenticationContext,
} from '@vs-online-only-app/state';
import styled from 'styled-components';
import SelectedConversationPlaceholder from '../components/selected-conversation/SelectedConversationPlaceholder';
import {
  CenteredLoader,
  useAssociateStore,
  useSidebarContext,
} from '@vs-online-only-app/ui';
import { useSelectedHeroConversation } from '../hooks/useSelectedHeroConversation';
import useSetActiveChatType from '../hooks/useSetActiveChatType';
import { Alert } from '@mui/material';

type ContainerProps = {
  $isSidebarOpen?: boolean;
  $showStoreClosedBanner?: boolean;
};

const Container = styled.div<ContainerProps>`
  display: grid;
  grid-template-columns: 1fr ${({ $isSidebarOpen }) =>
      $isSidebarOpen ? 3 : 3.5}fr;
  grid-template-rows: ${({ $showStoreClosedBanner }) =>
    $showStoreClosedBanner ? 'auto 1fr' : '1fr'};
  overflow: auto;
`;

const SelectionConversationSectionWrapper = styled.div`
  display: flex;
  background-color: white;
  border-left: 1px solid ${({ theme }) => theme.palette.divider};
  overflow: auto;
`;

const StoreClosedBanner = styled(Alert)`
  grid-column: 1/3;
`;

const ChatsView: FC = () => {
  const { user } = useContext(AuthenticationContext);
  const selectedConversation = useSelectedHeroConversation();
  const useAddConversationParticipantMutation = useAddConversationParticipant();
  const { user: associate } = useContext(AuthenticationContext);
  useSetActiveChatType();

  // Fetch associate settings and saved replies
  useGetAssociateSettings();
  useGetAssociateSavedReplies({ type: 'all' });
  useGetAssociateSavedReplies({ type: 'team' });
  useGetAssociateSavedReplies({ type: 'custom' });

  useEffect(() => {
    if (
      associate &&
      selectedConversation &&
      selectedConversation?.archiveAttributes
    ) {
      useAddConversationParticipantMutation.mutate({
        conversationId: selectedConversation.id,
        userToAdd: associate.username,
      });
    }
  }, [selectedConversation, associate]);

  const { isStoreOpen, formattedTimeUntilStoreOpens } = useAssociateStore(user);
  const showStoreClosedBanner = useMemo(
    () =>
      user &&
      user.approvalStatus === AssociateApprovalStatus.Approved &&
      !isStoreOpen,
    [isStoreOpen, user],
  );

  const { isOpen: isSidebarOpen } = useSidebarContext();

  return (
    <Container
      $isSidebarOpen={isSidebarOpen}
      $showStoreClosedBanner={showStoreClosedBanner}
    >
      {showStoreClosedBanner && (
        <StoreClosedBanner severity="info">
          Your store closed and will open in{' '}
          <strong>{formattedTimeUntilStoreOpens}</strong>. Then you can go
          online and start chatting with customers.
        </StoreClosedBanner>
      )}
      <AllConversationsSection />
      <SelectionConversationSectionWrapper>
        <Suspense fallback={<CenteredLoader large />}>
          {selectedConversation &&
          user?.approvalStatus === AssociateApprovalStatus.Approved ? (
            <SelectedConversationSection />
          ) : (
            <SelectedConversationPlaceholder />
          )}
        </Suspense>
      </SelectionConversationSectionWrapper>
    </Container>
  );
};

export default ChatsView;
